import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { readStoredAdminFlag } from "../authAdmin";
import MarketplaceCoinIcon from "./MarketplaceCoinIcon";
import { fetchMarketplaceShop, purchaseMarketplaceItem } from "./marketplaceApi";
import { notifyMarketplaceBalanceChanged } from "./MarketplaceCurrencyBadge";
import MarketplaceAdminTransactionHistory from "./MarketplaceAdminTransactionHistory";
import {
  clearCosmeticPreview,
  setCosmeticPreview,
  useCosmeticPreview,
} from "./cosmeticPreviewStore";
import { isImpersonating, isViewerAdministrator } from "./userMatrixStorage";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { canAccessShop } from "./shopAccess";
import "./marketplaceShop.css";
import "./commentAuthorDisplay.css";

const TIER_ORDER = ["starter", "rare", "legendary"];

const SHOP_CATEGORIES = [
  { id: "avatars", label: "Profile icons" },
  { id: "usernames", label: "Usernames" },
  { id: "themes", label: "Themes" },
];

const TIER_META = {
  starter: {
    label: "Starter",
    blurb: "Affordable pixel picks to get started.",
  },
  rare: {
    label: "Rare",
    blurb: "Glowing frames, neon nameplates, and rich board palettes — worth saving for.",
  },
  legendary: {
    label: "Legendary",
    blurb: "Animated auras, shimmering names, and full UI reskins. The ones everyone notices.",
  },
};

function formatPrice(n) {
  return Math.max(0, Math.trunc(Number(n) || 0)).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

function itemTier(item) {
  return item?.tier || "starter";
}

function tierLabel(item) {
  return item?.tierLabel || TIER_META[itemTier(item)]?.label || "Starter";
}

function previewHintForType(type) {
  if (type === "boardTheme") return "Try on the whole board";
  if (type === "avatar") return "Try on your sidebar icon";
  if (type === "usernameStyle") return "Try on your sidebar name";
  return "Try on";
}

function ShopItemCard({ item, owned, balance, buyingId, onBuy, previewingId, onPreview, onExitPreview }) {
  const tier = itemTier(item);
  const isOwned = owned.has(item.id);
  const canAfford = balance >= item.price;
  const busy = buyingId === item.id;
  const isLegendary = tier === "legendary";
  const isPreviewing = previewingId === item.id;

  return (
    <li
      className={`marketplace-shop-card marketplace-shop-card--tier-${tier}${
        isPreviewing ? " marketplace-shop-card--previewing" : ""
      }`}
    >
      <div className={`marketplace-shop-card-preview marketplace-shop-card-preview--tier-${tier}`}>
        {isLegendary ? <span className="marketplace-shop-sparkle" aria-hidden="true" /> : null}
        {item.type === "avatar" && item.imageDataUrl ? (
          <div
            className={`marketplace-shop-avatar-preview-wrap${item.auraClass ? ` ${item.auraClass}` : ""}${
              isLegendary ? " marketplace-shop-avatar-preview-wrap--legendary" : ""
            }`}
          >
            <img src={item.imageDataUrl} alt="" className="marketplace-shop-avatar-preview" />
          </div>
        ) : item.type === "boardTheme" && Array.isArray(item.previewColors) ? (
          <div
            className={`marketplace-shop-theme-preview marketplace-shop-theme-preview--tier-${tier}`}
            aria-hidden="true"
          >
            {item.previewColors.map((color, idx) => (
              <span key={idx} className="marketplace-shop-theme-preview-swatch" style={{ background: color }} />
            ))}
            <span className="marketplace-shop-theme-preview-label">Aa</span>
          </div>
        ) : item.cssClass ? (
          <span
            className={`marketplace-shop-style-preview marketplace-shop-style-preview--tier-${tier} ${item.cssClass}`}
          >
            Username
          </span>
        ) : null}
      </div>
      <div className="marketplace-shop-card-body">
        <div className="marketplace-shop-card-title-row">
          <h3 className="marketplace-shop-card-title">{item.name}</h3>
          <span className={`marketplace-shop-tier-badge marketplace-shop-tier-badge--${tier}`}>
            {tierLabel(item)}
          </span>
        </div>
        <p className="marketplace-shop-card-desc">{item.description}</p>
        <div className="marketplace-shop-card-actions">
          <button
            type="button"
            className={`marketplace-shop-preview-btn${isPreviewing ? " is-active" : ""}`}
            aria-pressed={isPreviewing}
            title={previewHintForType(item.type)}
            onClick={() => (isPreviewing ? onExitPreview() : onPreview(item))}
          >
            {isPreviewing ? "Exit preview" : "Preview"}
          </button>
          <div className="marketplace-shop-card-footer">
            <span className="marketplace-shop-price">
              <MarketplaceCoinIcon size={16} />
              {formatPrice(item.price)}
            </span>
            {isOwned ? (
              <span className="marketplace-shop-owned-badge">Owned</span>
            ) : (
              <button
                type="button"
                className={`marketplace-shop-buy-btn${tier === "legendary" ? " marketplace-shop-buy-btn--legendary" : ""}`}
                disabled={busy || !canAfford}
                onClick={() => onBuy(item.id)}
              >
                {busy ? "Buying…" : canAfford ? "Buy" : "Need more coins"}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ShopCarousel({
  title,
  subtitle,
  tier,
  items,
  owned,
  balance,
  buyingId,
  onBuy,
  previewingId,
  onPreview,
  onExitPreview,
}) {
  const trackRef = useRef(null);

  const scrollByDir = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.max(240, Math.round(el.clientWidth * 0.8)),
      behavior: "smooth",
    });
  };

  if (!items.length) return null;

  return (
    <section className={`marketplace-shop-tier-block marketplace-shop-tier-block--${tier}`}>
      <div className="marketplace-shop-section-head">
        <div className="marketplace-shop-section-copy">
          <h2 className="marketplace-shop-section-title">
            <span className={`marketplace-shop-tier-badge marketplace-shop-tier-badge--${tier}`}>
              {TIER_META[tier]?.label || tier}
            </span>
            {title}
          </h2>
          {subtitle ? <p className="marketplace-shop-section-subtitle">{subtitle}</p> : null}
        </div>
        <div className="marketplace-shop-carousel-nav">
          <button
            type="button"
            className="marketplace-shop-carousel-btn"
            aria-label={`Scroll ${title} left`}
            onClick={() => scrollByDir(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="marketplace-shop-carousel-btn"
            aria-label={`Scroll ${title} right`}
            onClick={() => scrollByDir(1)}
          >
            ›
          </button>
        </div>
      </div>
      <ul className="marketplace-shop-carousel" ref={trackRef}>
        {items.map((item) => (
          <ShopItemCard
            key={item.id}
            item={item}
            owned={owned}
            balance={balance}
            buyingId={buyingId}
            onBuy={onBuy}
            previewingId={previewingId}
            onPreview={onPreview}
            onExitPreview={onExitPreview}
          />
        ))}
      </ul>
    </section>
  );
}

function groupCatalogByTypeAndTier(catalog) {
  const avatars = catalog.filter((i) => i.type === "avatar");
  const styles = catalog.filter((i) => i.type === "usernameStyle");
  const themes = catalog.filter((i) => i.type === "boardTheme");

  const byTier = (items, typeLabel) =>
    TIER_ORDER.map((tier) => ({
      tier,
      title: `${typeLabel}`,
      subtitle: TIER_META[tier]?.blurb || "",
      items: items.filter((i) => itemTier(i) === tier),
    })).filter((g) => g.items.length > 0);

  return {
    avatarGroups: byTier(avatars, "Profile icons"),
    styleGroups: byTier(styles, "Username styles"),
    themeGroups: byTier(themes, "Board themes"),
  };
}

function ShopCategoryPanel({ categoryId, avatarGroups, styleGroups, themeGroups, carouselProps }) {
  if (categoryId === "avatars") {
    return avatarGroups.map((group) => (
      <ShopCarousel key={`avatar-${group.tier}`} {...group} {...carouselProps} />
    ));
  }

  if (categoryId === "usernames") {
    return styleGroups.map((group) => (
      <ShopCarousel key={`style-${group.tier}`} {...group} {...carouselProps} />
    ));
  }

  if (categoryId === "themes") {
    return (
      <>
        <p className="marketplace-shop-type-desc email-triggers-muted">
          Preview applies the theme to the whole board instantly (nothing is saved until you buy and equip). Starter
          themes are light palette swaps; Rare are rich dark boards; Legendary reskins chrome with custom atmosphere.
        </p>
        {themeGroups.map((group) => (
          <ShopCarousel key={`theme-${group.tier}`} {...group} {...carouselProps} />
        ))}
      </>
    );
  }

  return null;
}

export default function MarketplaceShopPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [balance, setBalance] = useState(0);
  const [owned, setOwned] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [activeCategory, setActiveCategory] = useState("avatars");
  const cosmeticPreview = useCosmeticPreview();
  const previewItem = cosmeticPreview?.item || null;

  const showAdminHistory =
    !isImpersonating() && (readStoredAdminFlag() || isViewerAdministrator());

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const data = await fetchMarketplaceShop();
      setBalance(Number(data.balance) || 0);
      setOwned(Array.isArray(data.owned) ? data.owned : []);
      setCatalog(Array.isArray(data.catalog) ? data.catalog : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not load shop.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onImpersonation = () => load();
    window.addEventListener("pb-impersonation-changed", onImpersonation);
    return () => {
      window.removeEventListener("pb-impersonation-changed", onImpersonation);
      clearCosmeticPreview();
    };
  }, [load]);

  const ownedSet = useMemo(() => new Set(owned), [owned]);
  const { avatarGroups, styleGroups, themeGroups } = useMemo(() => groupCatalogByTypeAndTier(catalog), [catalog]);

  const categoryCounts = useMemo(
    () => ({
      avatars: catalog.filter((i) => i.type === "avatar").length,
      usernames: catalog.filter((i) => i.type === "usernameStyle").length,
      themes: catalog.filter((i) => i.type === "boardTheme").length,
    }),
    [catalog]
  );

  const onPreview = useCallback((item) => {
    setCosmeticPreview(item);
  }, []);

  const onExitPreview = useCallback(() => {
    clearCosmeticPreview();
  }, []);

  const onBuy = async (itemId) => {
    setMessage("");
    setError("");
    setBuyingId(itemId);
    try {
      const data = await purchaseMarketplaceItem(itemId);
      setBalance(Number(data.balance) || 0);
      setOwned(Array.isArray(data.owned) ? data.owned : []);
      setMessage("Purchase complete! Apply your cosmetic in Settings → Profile.");
      notifyMarketplaceBalanceChanged();
      setHistoryRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Purchase failed.");
    } finally {
      setBuyingId(null);
    }
  };

  const carouselProps = {
    owned: ownedSet,
    balance,
    buyingId,
    onBuy,
    previewingId: previewItem?.id || null,
    onPreview,
    onExitPreview,
  };

  return (
    <div className="plugins-page marketplace-shop-page">
      <div className="marketplace-shop-inner">
      <div className="plugins-page-header">
        <h1 className="plugins-page-title">Shop</h1>
        <p className="plugins-page-lead">
          Spend V-coins on profile icons, username styles, and board themes. Preview anything before you buy — themes
          reskin the board live; icons and names show on your sidebar status. Equip purchases in{" "}
          <Link to="/settings">Settings → Profile</Link>.
        </p>
        <p className="marketplace-shop-balance-line">
          Your balance:{" "}
          <span className="marketplace-shop-balance-pill">
            <MarketplaceCoinIcon size={18} />
            {formatPrice(balance)}
          </span>
        </p>
      </div>

      <aside className="marketplace-shop-bug-banner" role="note">
        <span className="marketplace-shop-bug-banner-label">Bug hunting</span>
        <p className="marketplace-shop-bug-banner-text">
          Spot a real bug and you&apos;ll be <strong>manually awarded V-coins</strong>. It has to be
          clearly broken or incorrect behavior — not a feature you expected that simply isn&apos;t
          built yet.
        </p>
      </aside>

      {previewItem ? (
        <div className="marketplace-shop-preview-banner" role="status">
          <div className="marketplace-shop-preview-banner-copy">
            <span className="marketplace-shop-preview-banner-label">Previewing</span>
            <strong>{previewItem.name}</strong>
            <span className="marketplace-shop-preview-banner-hint">
              {previewItem.type === "boardTheme"
                ? " — look around the sidebar and board chrome"
                : previewItem.type === "avatar"
                  ? " — check your icon in the sidebar"
                  : " — check your name in the sidebar"}
            </span>
          </div>
          <button type="button" className="marketplace-shop-preview-banner-exit" onClick={onExitPreview}>
            Exit preview
          </button>
        </div>
      ) : null}

      {loading ? <p className="email-triggers-muted">Loading shop…</p> : null}
      {error ? (
        <p className="user-profile-settings-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="user-profile-settings-success" role="status">
          {message}
        </p>
      ) : null}

      {!loading && !error ? (
        <>
          <div className="marketplace-shop-category-tabs" role="tablist" aria-label="Shop categories">
            {SHOP_CATEGORIES.map((cat) => {
              const count = categoryCounts[cat.id] || 0;
              return (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  id={`marketplace-shop-tab-${cat.id}`}
                  aria-selected={activeCategory === cat.id}
                  aria-controls={`marketplace-shop-panel-${cat.id}`}
                  className={`marketplace-shop-category-tab${
                    activeCategory === cat.id ? " is-active" : ""
                  }`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.label}
                  <span className="marketplace-shop-category-tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {SHOP_CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              id={`marketplace-shop-panel-${cat.id}`}
              role="tabpanel"
              aria-labelledby={`marketplace-shop-tab-${cat.id}`}
              hidden={activeCategory !== cat.id}
              className="marketplace-shop-category-panel"
            >
              {activeCategory === cat.id ? (
                <ShopCategoryPanel
                  categoryId={cat.id}
                  avatarGroups={avatarGroups}
                  styleGroups={styleGroups}
                  themeGroups={themeGroups}
                  carouselProps={carouselProps}
                />
              ) : null}
            </div>
          ))}
        </>
      ) : null}

      {showAdminHistory ? (
        <MarketplaceAdminTransactionHistory refreshKey={historyRefreshKey} mode="admin" />
      ) : (
        <MarketplaceAdminTransactionHistory refreshKey={historyRefreshKey} mode="self" />
      )}

      <p className="plugins-page-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>
      </div>
    </div>
  );
}

export function MarketplaceShopGate() {
  if (!isPluginEnabled(PLUGIN_IDS.MARKETPLACE)) {
    return <Navigate to="/" replace />;
  }
  if (!canAccessShop()) {
    return <Navigate to="/" replace />;
  }
  return <MarketplaceShopPage />;
}
